import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Mail, FileText, Camera, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import TrialBanner from '../components/TrialBanner';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [lettersRes, subRes] = await Promise.all([
        axios.get(`${API}/letters/list`, { withCredentials: true }),
        axios.get(`${API}/subscription/status`, { withCredentials: true })
      ]);
      setLetters(lettersRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin' && !user.questionnaire_completed) {
      navigate('/questionnaire');
    } else if (user) {
      fetchData();
    }
  }, [user, navigate, fetchData]);

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <AppHeader />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <TrialBanner subscription={subscription} />

        <div className="mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Welcome back, {user?.name?.split(' ')[0]}</h2>
          <p className="text-[#737373]">Write every day. Stay connected every week.</p>
        </div>

        {subscription?.active && (
          <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-8" data-testid="usage-stats">
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
              <div className="flex items-center justify-between mb-2">
                <span className="label-text">Letters Used</span>
                <FileText className="w-5 h-5 text-[#B94A3E]" strokeWidth={1.5} />
              </div>
              <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}}>
                {subscription.letters_used}/{subscription.letters_limit === 999 ? '∞' : subscription.letters_limit}
              </div>
              <div className="mt-2 h-2 bg-[#E5E0D8] rounded-full overflow-hidden">
                <div className="h-full bg-[#B94A3E]" style={{width: `${Math.min((subscription.letters_used / subscription.letters_limit) * 100, 100)}%`}} />
              </div>
            </div>
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
              <div className="flex items-center justify-between mb-2">
                <span className="label-text">AI Photos Used</span>
                <Camera className="w-5 h-5 text-[#B94A3E]" strokeWidth={1.5} />
              </div>
              <div className="text-3xl font-light" style={{fontFamily: 'Spectral, serif'}}>
                {subscription.photos_used}/{subscription.photos_limit}
              </div>
              <div className="mt-2 h-2 bg-[#E5E0D8] rounded-full overflow-hidden">
                <div className="h-full bg-[#B94A3E]" style={{width: `${Math.min((subscription.photos_used / subscription.photos_limit) * 100, 100)}%`}} />
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
          <Link to="/letters">
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)] hover:shadow-[3px_3px_0px_rgba(26,26,26,0.12)] transition-all hover:-translate-y-1" data-testid="nav-letters-card">
              <FileText className="w-10 h-10 text-[#B94A3E] mb-4" strokeWidth={1.5} />
              <h3 className="text-xl mb-2" style={{fontFamily: 'Spectral, serif'}}>Write a Letter</h3>
              <p className="text-sm text-[#737373]">Create personalized letters with AI</p>
            </div>
          </Link>

          <Link to="/photos">
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)] hover:shadow-[3px_3px_0px_rgba(26,26,26,0.12)] transition-all hover:-translate-y-1" data-testid="nav-photos-card">
              <Camera className="w-10 h-10 text-[#B94A3E] mb-4" strokeWidth={1.5} />
              <h3 className="text-xl mb-2" style={{fontFamily: 'Spectral, serif'}}>Memory Photos</h3>
              <p className="text-sm text-[#737373]">Upload & generate AI memory photos</p>
            </div>
          </Link>

          <Link to="/calendar">
            <div className="bg-white border border-[#E5E0D8] rounded-md p-6 shadow-[2px_2px_0px_rgba(26,26,26,0.08)] hover:shadow-[3px_3px_0px_rgba(26,26,26,0.12)] transition-all hover:-translate-y-1" data-testid="nav-calendar-card">
              <CalendarIcon className="w-10 h-10 text-[#B94A3E] mb-4" strokeWidth={1.5} />
              <h3 className="text-xl mb-2" style={{fontFamily: 'Spectral, serif'}}>Letter Calendar</h3>
              <p className="text-sm text-[#737373]">See your writing streak & history</p>
            </div>
          </Link>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-md p-6 md:p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl md:text-2xl font-light tracking-tight" style={{fontFamily: 'Spectral, serif'}}>Recent Letters</h3>
            <Link to="/letters">
              <Button variant="outline" size="sm" className="border-[#E5E0D8]" data-testid="new-letter-btn">New Letter</Button>
            </Link>
          </div>
          {loading ? (
            <p className="text-[#737373]">Loading...</p>
          ) : letters.length === 0 ? (
            <div className="text-center py-12" data-testid="no-letters-message">
              <Mail className="w-16 h-16 mx-auto mb-4 text-[#E5E0D8]" />
              <p className="text-[#737373] mb-4">You haven't created any letters yet.</p>
              <Link to="/letters">
                <Button className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white" data-testid="create-first-letter-btn">Write Your First Letter</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {letters.slice(0, 5).map((letter) => (
                <Link key={letter._id} to={`/letters/${letter._id}`}>
                  <div className="border border-[#E5E0D8] rounded-md p-4 hover:bg-[#F9F7F3] transition-colors cursor-pointer" data-testid={`letter-${letter._id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium">{letter.occasion || 'Personal Letter'}</h4>
                      <span className={`text-xs label-text px-2 py-1 rounded ${letter.status === 'mailed' ? 'bg-green-50 text-green-700' : 'bg-[#E5E0D8]'}`}>
                        {letter.status}
                      </span>
                    </div>
                    <p className="text-sm text-[#737373] handwriting text-lg">{letter.preview}</p>
                    <p className="text-xs text-[#737373] mt-2">{new Date(letter.created_at).toLocaleDateString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
